import React, { useState } from "react";
import PropTypes from "prop-types";
import "./IncentiveCard.scss";
import { FormatAsDollars } from "./../../utils/Helpers/Format";
import { FormattedMessage } from "react-intl";

const CJK_TEXT_PATTERN = /[\u4E00-\u9FFF]/;
const RTL_TEXT_PATTERN = /[\u0600-\u06FF\u0750-\u077F]/;

const INCENTIVE_TYPE_MESSAGES = {
     "bill credit": { id: "incentives.type.billCredit", defaultMessage: "Bill Credit" },
     "charging discount": { id: "incentives.type.chargingDiscount", defaultMessage: "Charging Discount" },
     "curbside charging": { id: "incentives.type.curbsideCharging", defaultMessage: "Curbside Charging" },
     "discounted electric rate": { id: "incentives.type.discountedElectricRate", defaultMessage: "EV Pricing Plans" },
     "financing": { id: "incentives.type.financing", defaultMessage: "Financing" },
     "free equipment": { id: "incentives.type.freeEquipment", defaultMessage: "Free Equipment" },
     "hov lane": { id: "incentives.type.hovLane", defaultMessage: "HOV Lane" },
     "insurance discount": { id: "incentives.type.insuranceDiscount", defaultMessage: "Insurance Discount" },
     "parking exemption": { id: "incentives.type.parkingExemption", defaultMessage: "Parking Exemption" },
     "public charging": { id: "incentives.type.publicCharging", defaultMessage: "Public Charging" },
     "rebate": { id: "incentives.type.rebate", defaultMessage: "Vehicle Rebate" },
     "registration fee": { id: "incentives.type.registrationFee", defaultMessage: "Registration Fee" },
     "tax credit": { id: "incentives.type.taxCredit", defaultMessage: "Tax Credit" },
     "tax exemption": { id: "incentives.type.taxExemption", defaultMessage: "Tax Exemption" },
     "toll discount": { id: "incentives.type.tollDiscount", defaultMessage: "Toll Discount" },
     "vehicle retirement": { id: "incentives.type.vehicleRetirement", defaultMessage: "Vehicle Retirement" },
};

const IncentiveCard = (props) => {
     const [showMore, setShowMore] = useState(false);

     let incentive = props && props.incentive ? props.incentive : {};
     let eligibility = incentive.evaluation && incentive.evaluation.eligibility;

     if (eligibility === "ineligible") {
          return null;
     }

     if (incentive.grantor === "Federal") {
          incentive.typical_amount = incentive.typical_amount
               .toString()
               .replace("Up to", "");
     }

     let type = incentive.type;
     let typeMessage = type
          ? INCENTIVE_TYPE_MESSAGES[type.trim().toLowerCase()]
          : null;

     const getDescriptionMaxLength = (text) => {
          if (CJK_TEXT_PATTERN.test(text)) return 62;
          if (RTL_TEXT_PATTERN.test(text)) return 86;
          return 114;
     };

     const truncateText = (text, max = 25) => {
          if (!text) return "Nome";

          if (text.length > max) {
               return text.substring(0, max).trim();
          } else return text;
     };

     let name = incentive.grantor + " " + incentive.name || "";
     let description = incentive.description || "";

     let amountDescription =
          incentive.typical_amount && incentive.typical_amount !== "N/A"
               ? incentive.typical_amount.replace("-$", "-")
               : false;

     let amount =
          incentive.evaluation &&
          incentive.evaluation.amount_in_purchase &&
          parseInt(incentive.evaluation.amount_in_purchase, 10) > 0
               ? parseInt(incentive.evaluation.amount_in_purchase, 10)
               : 0;

     const handleShowClick = (e) => {
          e.preventDefault();
          e.stopPropagation();

          setShowMore((s) => !s);
     };

     let renderCard =
          incentive && !props.displayNone ? (
               <a
                    className={`${props.displayNone ? "displayNone" : "IncentiveCard"} ${
                         showMore ? "showAllCard" : ""
                    }`}
                    href={incentive.details_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-incentive-id={incentive.id}
               >
                    <div className="cardTopArea">
                         <div className="colorBar" />
                         <p className="title">
                              {typeMessage ? (
                                   <FormattedMessage
                                        id={typeMessage.id}
                                        defaultMessage={typeMessage.defaultMessage}
                                   />
                              ) : (
                                   type
                              )}
                         </p>

                         <p className="value">
                              {amount ? (
                                   FormatAsDollars(amount)
                              ) : amountDescription ? (
                                   amountDescription
                              ) : (
                                   <svg
                                        width="34"
                                        height="54"
                                        viewBox="0 0 34 54"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                   >
                                        <path
                                             d="M1.26869 53.979C0.868664 53.8632 0.571505 53.6422 0.468642 53.2424C0.434355 53.1161 0.422925 53.0109 0.480071 52.8952C0.91438 51.9376 1.33726 50.9801 1.783 50.0226C4.70887 43.6356 7.62332 37.2381 10.5492 30.8511C10.8349 30.2303 11.1092 29.6095 11.395 28.9887C11.4178 28.9256 11.4635 28.873 11.4521 28.7888C11.3607 28.7888 11.2578 28.7888 11.1664 28.7888C7.96619 28.7888 4.76602 28.7888 1.56584 28.7888C0.971526 28.7888 0.662939 28.5678 0.468642 28.0522C0.377209 27.8102 0.50293 27.6103 0.605792 27.4209C1.40584 25.9373 2.21731 24.4431 3.02878 22.9595C7.05186 15.5413 11.0864 8.12315 15.1094 0.704988C15.2923 0.368277 15.5323 0.105222 15.9323 0C21.2583 0 26.5844 0 31.9104 0C31.9218 0.0105222 31.9332 0.0315654 31.9446 0.0315654C32.619 0.14731 32.8933 0.90491 32.4932 1.49415C28.7445 6.92362 25.0071 12.3531 21.2698 17.7825C21.2355 17.8352 21.2012 17.8983 21.1555 17.9719C21.2583 18.014 21.3612 18.0035 21.4526 18.0035C25.1671 18.0035 28.8816 18.0035 32.5961 18.0035C33.1219 18.0035 33.4876 18.214 33.6362 18.6033C33.7619 18.9295 33.6933 19.2241 33.4647 19.4871C31.7275 21.3917 29.9903 23.2962 28.2416 25.2007C26.3672 27.2525 24.4928 29.3044 22.607 31.3562C20.9155 33.2081 19.224 35.06 17.521 36.9119C15.8295 38.7638 14.138 40.6157 12.4464 42.4677C10.8349 44.2354 9.21198 45.9926 7.60046 47.7603C5.82893 49.6964 4.05741 51.6325 2.28588 53.5791C2.10302 53.779 1.88586 53.9158 1.62299 54C1.49727 53.979 1.38298 53.979 1.26869 53.979Z"
                                             fill="#BDBDBD"
                                        />
                                   </svg>
                              )}
                         </p>

                         <p className="text">{name}</p>

                         <hr />
                    </div>
                    {description.length > 114 ? (
                         <p className="description">
                              {showMore ? description : truncateText(description, getDescriptionMaxLength(description))}
                              <button className="showBtn" onClick={handleShowClick}>
                                   {showMore ? (
                                        <FormattedMessage
                                             id="incentives.less"
                                             defaultMessage=" less"
                                             description="less"
                                        />
                                   ) : (
                                        <FormattedMessage
                                             id="incentives.more"
                                             defaultMessage="... more"
                                             description="more"
                                        />
                                   )}
                              </button>
                         </p>
                    ) : (
                         <p className="description">{description}</p>
                    )}
               </a>
          ) : null;

     return renderCard;
};

export default IncentiveCard;

IncentiveCard.propTypes = {
     incentive: PropTypes.object,
     compact: PropTypes.bool,
};
